import { useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { OverviewHeader } from '~/calendar/components/overview-header/OverviewHeader';
import { WeekSelector } from '~/calendar/components/week-selector/WeekSelector';
import { useCurrentOverviewDate } from '~/calendar/hooks/useCurrentOverviewDate';
import { WorkoutsOverviewSection } from '~/workout/components/workouts-overview-section/WorkoutsOverviewSection';
import { MealsOverviewSection } from '~/meal/components/meals-overview-section/MealsOverviewSection';
import { MealListOverviewSection } from '~/meal/components/meal-list-overview-section/MealListOverviewSection';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { startOfWeek, startOfDay, endOfDay, addDays, format } from 'date-fns';

export interface OverviewScreenProps {}

export const OverviewScreen = ({}: OverviewScreenProps) => {
  const router = useRouter();
  const selectedDate = useCurrentOverviewDate();
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const fromDate = startOfDay(weekDates[0]).toISOString();
  const toDate = endOfDay(weekDates[6]).toISOString();

  const { data: weekWorkouts = [] } = useLivePowerSyncQuery<{
    plannedAt: string | null;
  }>(db.Workout.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  const workoutDates = useMemo(
    () =>
      Array.from(
        new Set(
          weekWorkouts
            .filter((w) => w.plannedAt)
            .map((w) => (w.plannedAt as string).slice(0, 10)),
        ),
      ),
    [weekWorkouts],
  );

  return (
    <ScrollView
      className="flex-1 bg-background"
      showsVerticalScrollIndicator={false}
    >
      <View className="p-8 gap-6">
        <OverviewHeader.Body className="md:hidden" />
        <WeekSelector
          selectedDate={selectedDate}
          weekDates={weekDates}
          onDateSelect={(date) =>
            router.replace(`/${format(date, 'yyyy-MM-dd')}`)
          }
          workoutDates={workoutDates}
        />
        <View className="flex flex-col gap-6 md:flex-row md:gap-6">
          <View className="md:flex-1">
            <WorkoutsOverviewSection />
          </View>
          <View className="md:flex-1">
            <MealsOverviewSection />
          </View>
        </View>
        <MealListOverviewSection />
      </View>
    </ScrollView>
  );
};
